import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { api, API_BASE } from "@/lib/api";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";
import type { TopGame } from "@/types/api";

interface PriceDeal {
  shop: { id: number; name: string };
  price: { amount: number; amountInt: number; currency: string };
  regular: { amount: number; amountInt: number; currency: string };
  cut: number;
}

interface GameDeal {
  appid: string;
  name: string;
  steamPrice: number;
  deals: { store: string; price: number; saving: number; cut: number }[];
}

interface StoreStats {
  name: string;
  cheaperCount: number;
  avgSaving: number;
  bestDeal: { game: string; saving: number } | null;
}

function formatKRW(amount: number): string {
  return `₩${Math.round(amount).toLocaleString("ko-KR")}`;
}

type Tab = "games" | "stores";

export default function PriceComparePage() {
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [gameDeals, setGameDeals] = useState<GameDeal[]>([]);
  const [gamesAnalyzed, setGamesAnalyzed] = useState(0);
  const [tab, setTab] = useState<Tab>("games");
  const [selectedStore, setSelectedStore] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const topRes = await api.topGames("ko") as { games: TopGame[] } | TopGame[];
        const games = (Array.isArray(topRes) ? topRes : topRes.games || []).slice(0, 100);
        setGamesAnalyzed(games.length);

        // Fetch price-history for each game (parallel, max 10 concurrent)
        const allDeals: GameDeal[] = [];
        const chunks: TopGame[][] = [];
        for (let i = 0; i < games.length; i += 10) chunks.push(games.slice(i, i + 10));

        for (const chunk of chunks) {
          const results = await Promise.allSettled(
            chunk.map(async (game) => {
              const res = await fetch(`${API_BASE}/price-history?appid=${game.appid}&lang=ko`);
              const data = await res.json();
              if (!data.found || !data.prices?.[0]?.deals) return null;

              const steamDeal = data.prices[0].deals.find((d: PriceDeal) => d.shop.name === "Steam");
              const steamPrice = steamDeal?.regular?.amount || steamDeal?.price?.amount || 0;
              if (steamPrice === 0) return null;

              const cheaperDeals = data.prices[0].deals
                .filter((d: PriceDeal) => d.shop.name !== "Steam" && d.price.amount < steamPrice)
                .map((d: PriceDeal) => ({
                  store: d.shop.name,
                  price: d.price.amount,
                  saving: steamPrice - d.price.amount,
                  cut: d.cut,
                }))
                .sort((a: { saving: number }, b: { saving: number }) => b.saving - a.saving);

              if (cheaperDeals.length === 0) return null;

              return {
                appid: String(game.appid),
                name: game.name,
                steamPrice,
                deals: cheaperDeals,
              };
            })
          );

          for (const r of results) {
            if (r.status === "fulfilled" && r.value) allDeals.push(r.value);
          }
        }

        // Sort by max saving
        allDeals.sort((a, b) => b.deals[0].saving - a.deals[0].saving);
        setGameDeals(allDeals);
      } catch { /* */ }
      setLoading(false);
    })();
  }, []);

  // Compute store stats
  const priceStats = useMemo<StoreStats[]>(() => {
    const storeMap: Record<string, { count: number; totalSaving: number; bestGame: string; bestSaving: number }> = {};
    for (const game of gameDeals) {
      const seen = new Set<string>();
      for (const deal of game.deals) {
        if (seen.has(deal.store)) continue;
        seen.add(deal.store);
        if (!storeMap[deal.store]) storeMap[deal.store] = { count: 0, totalSaving: 0, bestGame: "", bestSaving: 0 };
        storeMap[deal.store].count++;
        storeMap[deal.store].totalSaving += deal.saving;
        if (deal.saving > storeMap[deal.store].bestSaving) {
          storeMap[deal.store].bestSaving = deal.saving;
          storeMap[deal.store].bestGame = game.name;
        }
      }
    }
    return Object.entries(storeMap)
      .map(([name, s]) => ({
        name,
        cheaperCount: s.count,
        avgSaving: s.totalSaving / s.count,
        bestDeal: s.bestGame ? { game: s.bestGame, saving: s.bestSaving } : null,
      }))
      .sort((a, b) => b.cheaperCount - a.cheaperCount);
  }, [gameDeals]);

  const filteredGames = useMemo(() => {
    if (!selectedStore) return gameDeals;
    return gameDeals.filter((g) => g.deals.some((d) => d.store === selectedStore));
  }, [gameDeals, selectedStore]);

  if (loading) return <Spinner />;

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 flex-1 w-full">
      {/* Description */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mt-6 mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
          </div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{t.priceCompare?.title || "스토어 가격 비교"}</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">
          Steam 인기 게임 {gamesAnalyzed}개를 기준으로, 다른 스토어에서 Steam보다 저렴한 게임을 찾아 비교해요.
        </p>
      </div>

      {/* Summary 3 cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
        <div className="bg-card-bg dark:bg-card-bg rounded-xl border border-gray-200 dark:border-card-border p-3 sm:p-4 text-center">
          <p className="text-[10px] sm:text-[11px] text-gray-400 dark:text-muted mb-0.5">{t.priceCompare?.analysis || "분석"}</p>
          <p className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">{gamesAnalyzed}</p>
        </div>
        <div className="bg-card-bg dark:bg-card-bg rounded-xl border border-gray-200 dark:border-card-border p-3 sm:p-4 text-center">
          <p className="text-[10px] sm:text-[11px] text-gray-400 dark:text-muted mb-0.5">{t.priceCompare?.cheaperThanSteam || "Steam보다 싼"}</p>
          <p className="text-lg sm:text-2xl font-black text-emerald-600">{gameDeals.length}</p>
        </div>
        <div className="bg-card-bg dark:bg-card-bg rounded-xl border border-gray-200 dark:border-card-border p-3 sm:p-4 text-center">
          <p className="text-[10px] sm:text-[11px] text-gray-400 dark:text-muted mb-0.5">{t.priceCompare?.store || "스토어"}</p>
          <p className="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">{priceStats.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        <button onClick={() => { setTab("games"); setSelectedStore(null); }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "games" ? "bg-gray-900 text-white" : "bg-card-bg dark:bg-card-bg border border-gray-200 dark:border-card-border text-gray-500 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{t.priceCompare?.gameComparison || "게임별 비교"}</button>
        <button onClick={() => setTab("stores")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "stores" ? "bg-gray-900 text-white" : "bg-card-bg dark:bg-card-bg border border-gray-200 dark:border-card-border text-gray-500 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{t.priceCompare?.storeRanking || "스토어 순위"}</button>
      </div>

      {/* Games tab */}
      {tab === "games" && (
        <>
          {/* Store filter pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
            <button onClick={() => setSelectedStore(null)} className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${!selectedStore ? "bg-emerald-600 text-white" : "bg-card-bg dark:bg-card-bg border border-gray-200 dark:border-card-border text-gray-600 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{t.common?.all || "전체"} ({gameDeals.length})</button>
            {priceStats.map((s) => (
              <button key={s.name} onClick={() => setSelectedStore(selectedStore === s.name ? null : s.name)} className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${selectedStore === s.name ? "bg-emerald-600 text-white" : "bg-card-bg dark:bg-card-bg border border-gray-200 dark:border-card-border text-gray-600 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{s.name} ({s.cheaperCount})</button>
            ))}
          </div>

          {/* Deals table */}
          <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm overflow-hidden mb-6">
            <div className="grid grid-cols-[1fr_70px_70px] sm:grid-cols-[1fr_110px_110px_90px] gap-1 sm:gap-2 px-3 sm:px-4 py-3 bg-inner-bg dark:bg-inner-bg border-b border-gray-200 dark:border-card-border">
              <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 dark:text-muted uppercase tracking-wider">{t.common?.game || "게임"}</span>
              <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 dark:text-muted uppercase tracking-wider text-right hidden sm:block">Steam</span>
              <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 dark:text-muted uppercase tracking-wider text-right">{t.priceCompare?.lowestPrice || "최저가"}</span>
              <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 dark:text-muted uppercase tracking-wider text-right">{t.priceCompare?.savings || "절약"}</span>
            </div>

            {filteredGames.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400 dark:text-muted">{t.priceCompare?.noDealsForStore || "이 스토어의 할인 게임이 없어요."}</div>
            ) : (
              filteredGames.map((game, idx) => {
                const bestDeal = selectedStore ? game.deals.find((d) => d.store === selectedStore)! : game.deals[0];
                return (
                  <Link key={game.appid} to={`/game/${game.appid}`} className={`grid grid-cols-[1fr_70px_70px] sm:grid-cols-[1fr_110px_110px_90px] gap-1 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 items-center hover:bg-inner-bg dark:hover:bg-hover-bg transition-colors ${idx < filteredGames.length - 1 ? "border-b border-inner-border dark:border-card-border" : ""}`}>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">{game.name}</p>
                      <p className="text-[10px] sm:text-[11px] text-gray-400 dark:text-muted truncate mt-0.5">
                        {selectedStore || bestDeal.store}
                        {!selectedStore && game.deals.length > 1 && <span className="text-gray-300 dark:text-[#2a3f5f]"> 외 {game.deals.length - 1}</span>}
                      </p>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-muted tabular-nums text-right line-through decoration-gray-300 hidden sm:block">{formatKRW(game.steamPrice)}</p>
                    <p className="text-[11px] sm:text-sm font-bold text-gray-900 dark:text-white tabular-nums text-right">{formatKRW(bestDeal.price)}</p>
                    <p className="text-[11px] sm:text-sm font-bold text-emerald-600 tabular-nums text-right">-{formatKRW(bestDeal.saving)}</p>
                  </Link>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Stores tab */}
      {tab === "stores" && (
        <div className="flex flex-col gap-3 mb-6">
          {priceStats.map((store, idx) => (
            <button key={store.name} onClick={() => { setTab("games"); setSelectedStore(store.name); }} className={`w-full text-left bg-card-bg dark:bg-card-bg rounded-xl border p-4 hover:shadow-md transition-all ${idx === 0 ? "border-emerald-200 bg-emerald-50/50" : "border-gray-200 dark:border-card-border"}`}>
              <div className="flex items-center gap-4">
                <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-sm font-black ${idx === 0 ? "bg-emerald-600 text-white" : idx === 1 ? "bg-gray-700 text-white" : idx === 2 ? "bg-gray-500 text-white" : "bg-gray-200 text-gray-600"}`}>{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{store.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-500 dark:text-muted"><span className="font-bold text-emerald-600">{store.cheaperCount}</span>/{gamesAnalyzed} {t.priceCompare?.gamesCheaper || "게임 더 저렴"}</span>
                    {store.bestDeal && <span className="text-[11px] text-gray-400 dark:text-muted hidden sm:inline truncate">{t.priceCompare?.max || "최대"} {store.bestDeal.game} (-{formatKRW(store.bestDeal.saving)})</span>}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-base font-black text-emerald-600 tabular-nums">{formatKRW(store.avgSaving)}</p>
                  <p className="text-[10px] text-gray-400 dark:text-muted">{t.priceCompare?.avgSavings || "평균 절약"}</p>
                </div>
                <div className="shrink-0 w-16 hidden sm:block">
                  <div className="w-full h-1.5 bg-hover-bg dark:bg-background rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${idx === 0 ? "bg-emerald-500" : "bg-gray-300"}`} style={{ width: `${Math.max((store.cheaperCount / gamesAnalyzed) * 100, 8)}%` }} />
                  </div>
                  <p className="text-[9px] text-gray-400 dark:text-muted mt-0.5 text-center tabular-nums">{Math.round((store.cheaperCount / gamesAnalyzed) * 100)}%</p>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-300 shrink-0"><polyline points="9 18 15 12 9 6" /></svg>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="pb-6" />
      <AdBanner className="my-6" />
    </div>
  );
}
